import { AuthScope, authScope, authStorageKey, clearStoredSession, readRefreshToken } from "./authSession";
import axios, { AxiosInstance } from "axios";

import url from "../server";
import { showIpBanNotice } from "./ipBanNotice";


const sessions = { seller: { token: null as string | null, pending: null as Promise<string | null> | null, error: null as any, generation: 0, expired: false }, customer: { token: null as string | null, pending: null as Promise<string | null> | null, error: null as any, generation: 0, expired: false } };

export const setAuthenticatedAccessToken = (token: string, scope: AuthScope = authScope()) => {
  const state = sessions[scope]; state.generation++; state.token = token; state.pending = null; state.error = null; state.expired = false;
};

declare module "axios" {
  interface AxiosRequestConfig<D = any> {
    // Pages such as a bill offer login without losing the requested destination.
    handleAuthErrorLocally?: boolean;
    authScope?: AuthScope;
  }
}


const api: AxiosInstance = axios.create({

  baseURL: "",

  withCredentials: true,

  // Axios preserves JSON types for objects and detects FormData/URLSearchParams.
});



// 요청

api.interceptors.request.use(

  async (config) => {

    const scope = config.authScope || authScope();
    config.authScope = scope;
    config.headers["X-Fanbird-Session"] = scope;
    const token = await tokenReturn(config.handleAuthErrorLocally, scope);
    if (!token) throw Object.assign(new Error("로그인이 필요합니다."), { code: "AUTH_REQUIRED" });
    if (token) config.headers.Authorization = `Bearer ${token}`;

    return config;

  },

  (error) => Promise.reject(error)

);



// 응답

api.interceptors.response.use(

  (res) => res,

  async (error) => {

    showIpBanNotice(error);

    const originalRequest = error.config;
    if (

      originalRequest && error.response?.status === 401 && !originalRequest._retry

    ) {

      originalRequest._retry = true; // 무한 루프 방지



      const scope: AuthScope = originalRequest.authScope || authScope();
      sessions[scope].token = null;

      const newToken = await refresh(scope);

      if (newToken) {

        // console.log("새 토큰: ", newToken);

        originalRequest.headers.Authorization = `Bearer ${newToken}`;

        return api(originalRequest); // 갱신된 토큰으로 재요청

      } else {

        if (sessions[scope].error && sessions[scope].error.response?.status !== 401) return Promise.reject(sessions[scope].error);
      }

    }

    return Promise.reject(error);

  }

);



/** 토큰 검사 */

const tokenReturn = async (handleLocally = false, scope: AuthScope = authScope()): Promise<string | null> => {
  const state = sessions[scope];
  const token = state.token || await refresh(scope);
  if (!token) {
    if (state.error && state.error.response?.status !== 401) throw state.error;
    if (!handleLocally && !state.expired) {
      state.expired = true; alert("로그인이 만료되었습니다. 재로그인 해주세요.");
      logout(scope === 'seller' ? 'admin' : undefined);
    }
  }
  return token;
};
const refresh = (scope: AuthScope = authScope()): Promise<string | null> => {
  const state = sessions[scope];
  if (!state.pending) {
    const request = requestRefresh(scope, state.generation).finally(() => { if (state.pending === request) state.pending = null; });
    state.pending = request;
  }
  return state.pending;
};
export const restoreSession = (scope: AuthScope = authScope()) => refresh(scope);
const requestRefresh = async (scope: AuthScope, generation: number): Promise<string | null> => {
  const state = sessions[scope];
  try {
    const res = await axios.post(url.Communication_url.refresh, { refreshToken: readRefreshToken(scope) }, {
      headers: { "Content-Type": "application/x-www-form-urlencoded", "X-Fanbird-Session": scope }, withCredentials: true,
    });
    if (generation !== state.generation) return state.token;
    if (res.data.newRefreshToken) {
      const key = authStorageKey('key', scope);
      (localStorage.getItem(key) ? localStorage : sessionStorage).setItem(key, res.data.newRefreshToken);
    }
    state.error = null; state.expired = false; state.token = res.data.accessToken;
    return state.token;
  } catch (error) {
    if (generation !== state.generation) return state.token;
    state.error = error;
    if (axios.isAxiosError(error) && error.response?.status === 401) { state.token = null; clearStoredSession(scope); }
    return null;
  }
};

/** 로그아웃  */

export const logout = (isAdmin?: string) => {
  const scope: AuthScope = isAdmin ? 'seller' : 'customer';
  const state = sessions[scope]; state.generation++; state.pending = null; state.token = null;
  clearStoredSession(scope);
  window.location.href = isAdmin ? "/admin/login" : "/";
};

export default api;

